import * as React from 'react';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Dropdown, IDropdown, IDropdownOption } from 'office-ui-fabric-react';

import { ApiUrls } from '@app/AppConstants';
import { DestinationRestrictionGroupModel, PersonGroupDestinationRestrictionModel } from '@app/Models/WebApiModels';

import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { TextCell } from '@app/Components/CellFormatters/TextCell';
import ApiService from '@app/Services/ApiService';
import { ViewMode } from '@app/Components/CellFormatters/ViewMode';

type InlineDestinationGroupDropdownCellProps = {
    value: string;
    row: PersonGroupDestinationRestrictionModel;
    onChange: Function;
    isReadOnly?: boolean;
    loader: PromiseCompletion;
};

@observer
export class InlineDestinationGroupDropdownCell extends React.Component<InlineDestinationGroupDropdownCellProps, {}> {
    store: InlineDestinationGroupDropdownCellStore;

    constructor(props: InlineDestinationGroupDropdownCellProps) {
        super(props);
        this.store = new InlineDestinationGroupDropdownCellStore(props);
    }

    componentDidUpdate(prevProps: InlineDestinationGroupDropdownCellProps) {
        if (this.props.row !== prevProps.row) {
            this.store.updateProps(this.props);
        }
    }

    render() {
        if (this.store.mode === ViewMode.view) {
            return <TextCell value={this.store.selectedName} onClick={this.store.toEditMode} />;
        } else {
            return (
                <Dropdown
                    notifyOnReselect={true}
                    options={this.store.groupOptions}
                    onChange={this.store.onChange}
                    componentRef={this.store.ref}
                    onDismiss={this.store.onMouseLeave}
                    selectedKey={this.store.selected}
                />
            );
        }
    }
}

class InlineDestinationGroupDropdownCellStore {
    @observable selected: string;
    @observable mode: string = ViewMode.view;
    @observable _initValue: string;
    @observable groups: DestinationRestrictionGroupModel[];
    private _loader: PromiseCompletion;

    ref: React.RefObject<IDropdown> = React.createRef();
    private _currentProps: InlineDestinationGroupDropdownCellProps;

    constructor(props: InlineDestinationGroupDropdownCellProps) {
        this.updateProps(props);
        this.loadDestinationRestrictionGroup();
    }

    @action
    toEditMode = () => {
        if (this._currentProps.isReadOnly) return;
        this.mode = ViewMode.edit;
        setTimeout(() => {
            this.ref.current && this.ref.current.focus(true);
        }, 0);
    };

    @action
    onMouseLeave = () => {
        this.mode = ViewMode.view;
    };

    @action
    onChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        this.mode = ViewMode.view;
        if (option && this.selected !== option.key) {
            this.selected = '' + option.key;
            this._currentProps.row.destinationRestrictionGroupId = this.selected;
            this._currentProps.onChange(this._currentProps.row);
        }
    };

    @action
    async loadDestinationRestrictionGroup(){
        const { data: groups } = await ApiService.getTypedData<DestinationRestrictionGroupModel[]>(ApiUrls.DestinationRestrictionGroups, null, { completion: this._loader});
        this.groups = groups;
    }

    @computed
    get selectedName() {
        if (this.selected) {
            if(!this.groups) return; 
            const selected = this.groups.find((group:DestinationRestrictionGroupModel) => group.id === this.selected);
            return selected && selected.name || '';
        }
    }

    @action
    updateProps(props: InlineDestinationGroupDropdownCellProps) {
        this._initValue = props.value || '';
        this._currentProps = { ...props };
        this.selected = props.value;
        this._loader = props.loader;
    }

    @computed
    get groupOptions(){
        return this.groups && this.groups.map(key =>{
            return {key: key.id, text: key.name};
        });
    }
}
